import { GraviButton, Horizontal, Texto } from '@gravitate-js/excalibrr'
import { DatePicker, Divider, Switch, message } from 'antd'
import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import type { EffectiveDateFormProps } from '../types'

export function EffectiveDateForm({ rate, sortedHistory, onFinish, loading }: EffectiveDateFormProps) {
  // Add check for rate existence
  const calculateMinimumDate = () => {
    // Handle cases where rate is null or history is empty/invalid
    if (!rate) {
      return dayjs() // Default to today if no rate context
    }
    if (sortedHistory.length === 0) {
      return dayjs(0) // No history, allow any date
    }

    const currentIndex = sortedHistory.findIndex((r) => r.id === rate.id)

    // If rate not found in history or it's the chronologically first rate (last in descending sort)
    if (currentIndex === -1 || currentIndex === sortedHistory.length - 1) {
      return dayjs(0) // Allow any past date
    }

    // The previous rate is the next one in the sorted array (since it's sorted descending)
    const previousRate = sortedHistory[currentIndex + 1]

    if (!previousRate) {
      // Should not happen due to the check above, but fallback to allow any date
      return dayjs(0)
    }

    // If previous rate has an end date, minimum is *that end date*
    if (previousRate.effective_to) {
      return dayjs(previousRate.effective_to)
    }

    // Otherwise, minimum is day after previous rate's start date
    return dayjs(previousRate.effective_from).add(1, 'day')
  }

  const minimumAllowedDate = calculateMinimumDate()

  // Determine the initial effective date
  const determineInitialDate = () => {
    if (minimumAllowedDate.isSame(dayjs(0))) {
      // If minimum date is epoch (first rate), default to the rate's *current* effective_from
      return rate ? dayjs(rate.effective_from) : dayjs() // Fallback to today if rate somehow null
    }
    // Otherwise, default to the calculated minimum allowed date
    return minimumAllowedDate
  }
  const initialEffectiveDate = determineInitialDate()

  // Get initial hasEndDate state from localStorage or fallback to rate's state
  const getInitialHasEndDate = () => {
    const storedValue = localStorage.getItem('baseFreight_hasEndDate')
    if (storedValue !== null) {
      return storedValue === 'true'
    }
    return !!rate?.effective_to
  }

  const [localEffectiveDate, setLocalEffectiveDate] = useState(initialEffectiveDate)
  const [hasEndDate, setHasEndDate] = useState(getInitialHasEndDate())
  const [localEffectiveTo, setLocalEffectiveTo] = useState<dayjs.Dayjs | null>(
    rate?.effective_to ? dayjs(rate.effective_to) : null
  )

  // Update localStorage when hasEndDate changes
  const handleHasEndDateChange = (checked: boolean) => {
    setHasEndDate(checked)
    localStorage.setItem('baseFreight_hasEndDate', checked.toString())
    if (!checked) {
      setLocalEffectiveTo(null)
    }
  }

  // Reset state if the rate changes (e.g., user clicks 'Change Effective' on a different tab)
  useEffect(() => {
    if (rate) {
      const newMinDate = calculateMinimumDate()
      // Determine the default start date based on whether it's the first rate
      const newInitialDate = newMinDate.isSame(dayjs(0))
        ? dayjs(rate.effective_from) // Default to current effective_from for first rate
        : newMinDate // Default to min date for subsequent rates
      setLocalEffectiveDate(newInitialDate)

      // Don't update hasEndDate here as we want to preserve the user's preference
      setLocalEffectiveTo(rate.effective_to ? dayjs(rate.effective_to) : null)
    }
  }, [rate, sortedHistory])

  const handleEffectiveToChange = (date: dayjs.Dayjs | null) => {
    if (date && date.isBefore(localEffectiveDate)) {
      message.error('End date cannot be before start date')
      return
    }
    setLocalEffectiveTo(date)
  }

  return (
    <div style={{ padding: '8px 0' }}>
      <Texto category='p1' style={{ paddingBottom: 8 }}>
        Select New Effective Date
      </Texto>

      <DatePicker
        format='MM/DD/YYYY'
        value={localEffectiveDate}
        onChange={(date) => setLocalEffectiveDate(date)}
        disabledDate={(current) => current?.isBefore(minimumAllowedDate, 'day')}
        style={{ width: '100%', marginBottom: 16 }}
      />

      <Horizontal verticalCenter gap={8} style={{ marginBottom: 16 }}>
        <Switch checked={hasEndDate} onChange={handleHasEndDateChange} />
        <Texto>{hasEndDate ? 'Set End Date' : 'No End Date'}</Texto>
      </Horizontal>

      {hasEndDate && (
        <DatePicker
          format='MM/DD/YYYY'
          value={localEffectiveTo}
          onChange={handleEffectiveToChange}
          disabledDate={(current) =>
            current && (current.isBefore(localEffectiveDate, 'day') || current.isBefore(minimumAllowedDate, 'day'))
          }
          style={{ width: '100%', marginBottom: 16 }}
        />
      )}

      <Divider />
      <Horizontal justifyContent='flex-end' gap={8}>
        <GraviButton
          appearance='outlined'
          onClick={() => {
            onFinish('cancel')
          }}
        >
          Cancel
        </GraviButton>
        <GraviButton
          appearance='solid'
          theme2
          loading={loading}
          onClick={() => {
            // Add validation before saving
            if (hasEndDate && localEffectiveTo && localEffectiveTo.isBefore(localEffectiveDate)) {
              message.error('End date cannot be before start date')
              return
            }
            onFinish(
              hasEndDate
                ? JSON.stringify({
                    effective_from: localEffectiveDate.format('YYYY-MM-DD'),
                    effective_to: localEffectiveTo?.format('YYYY-MM-DD') || null,
                  })
                : localEffectiveDate.format('YYYY-MM-DD')
            )
          }}
        >
          Save Changes
        </GraviButton>
      </Horizontal>
    </div>
  )
}
